import { useEffect, useMemo, useState } from 'react';
import { apiService } from '../../services/api.service';
import { usePermissions } from '../../context/PermissionsContext';

type AccountOpt = { id: string; code: string; name: string; isPosting?: boolean };

type LineDraft = {
  key: string;
  accountId: string;
  debit: string;
  credit: string;
  description: string;
};

function emptyLine(): LineDraft {
  return {
    key: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    accountId: '',
    debit: '',
    credit: '',
    description: '',
  };
}

type Props = {
  legalEntityId: string;
  defaultDate: string;
  accounts: AccountOpt[];
  onDone: () => void;
  onCancel: () => void;
};

/**
 * Alta manual de asiento (DRAFT → opcional POST).
 * Usa POST /accounting/journal/draft y /journal/:id/post.
 */
export function ManualJournalForm({
  legalEntityId,
  defaultDate,
  accounts,
  onDone,
  onCancel,
}: Props) {
  const { can } = usePermissions();
  const canWrite = can('accounting', 'write');

  const [entryDate, setEntryDate] = useState(defaultDate);
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([emptyLine(), emptyLine()]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  useEffect(() => {
    setEntryDate(defaultDate);
  }, [defaultDate]);

  const postingAccounts = useMemo(
    () => accounts.filter((a) => a.isPosting !== false),
    [accounts],
  );

  const totals = useMemo(() => {
    let debit = 0;
    let credit = 0;
    for (const l of lines) {
      debit += Number(l.debit) || 0;
      credit += Number(l.credit) || 0;
    }
    return { debit, credit, balanced: Math.abs(debit - credit) < 0.005 };
  }, [lines]);

  const updateLine = (key: string, patch: Partial<LineDraft>) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };

  const buildPayload = () => {
    if (!description.trim()) throw new Error('Ingresá una descripción');
    if (!entryDate) throw new Error('Ingresá la fecha del asiento');
    const mapped = lines
      .map((l) => ({
        accountId: l.accountId,
        debit: Number(l.debit) || 0,
        credit: Number(l.credit) || 0,
        description: l.description.trim() || undefined,
      }))
      .filter((l) => l.accountId && (l.debit > 0 || l.credit > 0));
    if (mapped.length < 2) throw new Error('Se necesitan al menos 2 líneas con cuenta e importe');
    for (const l of mapped) {
      if (l.debit > 0 && l.credit > 0) {
        throw new Error('Cada línea debe tener debe o haber, no ambos');
      }
    }
    const d = mapped.reduce((s, l) => s + l.debit, 0);
    const c = mapped.reduce((s, l) => s + l.credit, 0);
    if (Math.abs(d - c) >= 0.005) {
      throw new Error(`Asiento desbalanceado (debe ${d.toFixed(2)} ≠ haber ${c.toFixed(2)})`);
    }
    return { legalEntityId, entryDate, description: description.trim(), lines: mapped };
  };

  const submit = async (andPost: boolean) => {
    if (!canWrite) {
      setError('No tenés permiso de escritura en Contabilidad');
      return;
    }
    setBusy(true);
    setError(null);
    setOkMsg(null);
    try {
      const payload = buildPayload();
      const draft = await apiService.createAccountingJournalDraft(payload);
      if (andPost && draft?.id) {
        await apiService.postAccountingJournal(draft.id);
        setOkMsg(`Asiento #${draft.entryNumber ?? 'POSTED'} contabilizado`);
      } else {
        setOkMsg('Asiento guardado en borrador');
      }
      setDescription('');
      setLines([emptyLine(), emptyLine()]);
      onDone();
    } catch (e: any) {
      const msg =
        e?.response?.data?.message?.message ??
        e?.response?.data?.message ??
        e.message;
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setBusy(false);
    }
  };

  if (!canWrite) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
        No tenés permiso para crear asientos manuales.
        <button type="button" className="ml-3 underline" onClick={onCancel}>
          Cerrar
        </button>
      </div>
    );
  }

  return (
    <form
      className="rounded-lg border bg-white p-4 space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        void submit(false);
      }}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Nuevo asiento manual</h3>
          <p className="text-sm text-gray-500">
            Debe = Haber. Se guarda como borrador o se contabiliza al instante.
          </p>
        </div>
        <button type="button" className="text-sm text-gray-600 underline" onClick={onCancel}>
          Cancelar
        </button>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}
      {okMsg && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
          {okMsg}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm">
          <span className="block text-xs text-gray-500">Fecha</span>
          <input
            type="date"
            required
            className="w-full border rounded-md px-2 py-1.5"
            value={entryDate}
            onChange={(e) => setEntryDate(e.target.value)}
          />
        </label>
        <label className="text-sm sm:col-span-2">
          <span className="block text-xs text-gray-500">Descripción</span>
          <input
            type="text"
            required
            className="w-full border rounded-md px-2 py-1.5"
            placeholder="Ej. Ajuste de cierre / reclasificación"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-gray-500 border-b">
              <th className="py-2 pr-2">Cuenta</th>
              <th className="py-2 pr-2 w-28">Debe</th>
              <th className="py-2 pr-2 w-28">Haber</th>
              <th className="py-2 pr-2">Detalle línea</th>
              <th className="py-2 w-10" />
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.key} className="border-b border-gray-100 align-top">
                <td className="py-2 pr-2">
                  <select
                    className="w-full min-w-[12rem] border rounded-md px-2 py-1.5"
                    value={l.accountId}
                    onChange={(e) => updateLine(l.key, { accountId: e.target.value })}
                  >
                    <option value="">Seleccionar…</option>
                    {postingAccounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.code} — {a.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="py-2 pr-2">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-full border rounded-md px-2 py-1.5 text-right"
                    value={l.debit}
                    onChange={(e) =>
                      updateLine(l.key, {
                        debit: e.target.value,
                        credit: e.target.value ? '' : l.credit,
                      })
                    }
                  />
                </td>
                <td className="py-2 pr-2">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-full border rounded-md px-2 py-1.5 text-right"
                    value={l.credit}
                    onChange={(e) =>
                      updateLine(l.key, {
                        credit: e.target.value,
                        debit: e.target.value ? '' : l.debit,
                      })
                    }
                  />
                </td>
                <td className="py-2 pr-2">
                  <input
                    type="text"
                    className="w-full border rounded-md px-2 py-1.5"
                    value={l.description}
                    onChange={(e) => updateLine(l.key, { description: e.target.value })}
                  />
                </td>
                <td className="py-2">
                  <button
                    type="button"
                    className="text-gray-400 hover:text-red-600 px-1"
                    disabled={lines.length <= 2}
                    onClick={() => setLines((prev) => prev.filter((x) => x.key !== l.key))}
                    aria-label="Quitar línea"
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          className="text-sm px-3 py-1.5 rounded-md border hover:bg-gray-50"
          onClick={() => setLines((prev) => [...prev, emptyLine()])}
        >
          + Línea
        </button>
        <div
          className={`text-sm tabular-nums ${totals.balanced ? 'text-emerald-700' : 'text-red-700'}`}
        >
          Debe {totals.debit.toLocaleString('es-AR', { minimumFractionDigits: 2 })} · Haber{' '}
          {totals.credit.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
          {totals.balanced ? ' · Balanceado' : ' · Desbalanceado'}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 justify-end border-t pt-3">
        <button
          type="submit"
          disabled={busy || !totals.balanced}
          className="px-4 py-2 text-sm rounded-md border bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          {busy ? 'Guardando…' : 'Guardar borrador'}
        </button>
        <button
          type="button"
          disabled={busy || !totals.balanced}
          onClick={() => void submit(true)}
          className="px-4 py-2 text-sm rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {busy ? 'Procesando…' : 'Guardar y contabilizar'}
        </button>
      </div>
    </form>
  );
}
